import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { proceduceService } from "../../../services/api/procedureService";
import { serviceService } from "../../../services/api/serviceService";
import FilterButtons from "./components/FilterButtons";
import CategoryGrid from "./components/CategoryGrid";
import CustomTable from "../components/CustomTable";
import Loading from "../components/Loading";
import MobilePatternHeader from "../components/MobilePatternHeader";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import TableSkeleton from "../components/TableSkeleton";
import { useDebouncedValue } from "../../../hooks/useDebounce";
import EmptyState from "../components/EmptyState";
import Skeleton from "../components/Skeleton";

const toSlug = (str) =>
  str
    .replace(/Đ/g, "D")
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const FilterButtonsSkeleton = () => (
  <div className="flex flex-wrap gap-2">
    {Array.from({ length: 8 }).map((_, i) => (
      <Skeleton key={i} className="h-8 w-24 rounded-full" />
    ))}
  </div>
);

/* -------------------- Component -------------------- */
const Guide = ({ isMobile }) => {
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const abortRef = useRef(null);
  const requestKeyRef = useRef("");
  const sentinelRef = useRef(null);
  const initialSearchParamsRef = useRef(searchParams);

  const [category, setCategory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const [services, setServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [bootLoading, setBootLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  /* -------------------- Abort / RequestKey -------------------- */
  const startRequest = (key) => {
    try {
      abortRef.current?.abort();
    } catch {}
    const controller = new AbortController();
    abortRef.current = controller;
    requestKeyRef.current = key;
    return controller.signal;
  };
  const isStale = (key) => key !== requestKeyRef.current;

  /* -------------------- Format rows -------------------- */
  const formatData = useCallback(
    (apiResponse = [], page) =>
      apiResponse.map((item, index) => ({
        id: itemsPerPage * (page - 1) + index + 1,
        category: item.categoryId?.categoryName || "--",
        ten: item.name || "--",
        ngayNopHoSo: item.submissionTime || "--",
        ngayThamDinh: item.reviewTime || "--",
        fileUrl: item.fileUrl || null,
        nav_id: item._id,
      })),
    [itemsPerPage],
  );

  /* -------------------- Fetch category -------------------- */
  const fetchCategory = useCallback(async () => {
    try {
      setBootLoading(true);
      const signal = startRequest("fetchCategory");
      const response = await serviceService.get_all_category(token_client, {
        signal,
      });
      if (isStale("fetchCategory")) return;

      const categories = (response?.categories || []).map((item) => ({
        id: item.id,
        name: item.name,
        image: item.image,
      }));
      setCategory(categories);

      const initialSearchParams = initialSearchParamsRef.current;
      const slug = initialSearchParams.get("category");
      const fromSearch = initialSearchParams.get("search");
      const found = categories.find((c) => toSlug(c.name) === slug);

      if (!isMobile) {
        if (found) setSelectedCategory(found);
        else if (!fromSearch && categories.length > 0)
          setSelectedCategory(categories[0]);
      } else {
        setSelectedCategory(found || null);
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi lấy danh mục");
    } finally {
      setBootLoading(false);
    }
  }, [token_client, isMobile]);

  /* -------------------- Fetch by category -------------------- */
  const fetchByCategory = useCallback(
    async ({ categoryId, page = 1, append = false }) => {
      try {
        const key = `byCategory:${categoryId}:${page}`;
        const signal = startRequest(key);
        if (!append) setListLoading(true);

        const response = await proceduceService.get_by_category(
          categoryId,
          page,
          itemsPerPage,
          token_client,
          { signal },
        );
        if (isStale(key)) return;

        const formatted = formatData(response?.data?.procedures, page);
        setServices((prev) => (append ? [...prev, ...formatted] : formatted));
        setTotalPages(response?.data?.totalPages || 1);
        setCurrentPage(Number(response?.data?.currentPage) || page);
      } catch (error) {
        if (error?.name !== "AbortError") {
          toast.dismiss();
          toast.error("Lỗi khi lấy dữ liệu theo danh mục");
        }
      } finally {
        setListLoading(false);
        setLoadingMore(false);
      }
    },
    [formatData, itemsPerPage, token_client],
  );

  /* -------------------- Fetch by search (desktop) -------------------- */
  const fetchBySearch = useCallback(
    async ({ q, page = 1 }) => {
      try {
        const key = `bySearch:${q}:${page}`;
        const signal = startRequest(key);
        setListLoading(true);

        const response = await proceduceService.get(q, token_client, {
          signal,
        });
        if (isStale(key)) return;

        setServices(formatData(response?.data?.procedures, 1));
        setTotalPages(response?.data?.totalPages || 1);
        setCurrentPage(1);
      } catch (error) {
        if (error?.name !== "AbortError") {
          toast.dismiss();
          toast.error("Lỗi khi tìm kiếm");
        }
      } finally {
        setListLoading(false);
      }
    },
    [formatData, token_client],
  );

  /* -------------------- Desktop: apply từ URL -------------------- */
  useEffect(() => {
    if (isMobile) return;
    if (bootLoading) return;

    const slug = searchParams.get("category");
    const q = searchParams.get("search")?.trim() || "";
    const page = Number(searchParams.get("page")) || 1;

    setSearchTerm(q);

    if (q) {
      setSelectedCategory(null);
      fetchBySearch({ q, page: 1 });
      return;
    }

    if (slug && category.length > 0) {
      const found = category.find((c) => toSlug(c.name) === slug);
      setSelectedCategory(found || null);
      if (found?.id) fetchByCategory({ categoryId: found.id, page });
      return;
    }

    if (!slug && category.length > 0) {
      setSelectedCategory(category[0]);
      fetchByCategory({ categoryId: category[0].id, page });
    }
  }, [
    isMobile,
    bootLoading,
    searchParams,
    category,
    fetchByCategory,
    fetchBySearch,
  ]);

  /* -------------------- Load category lần đầu -------------------- */
  useEffect(() => {
    fetchCategory();
    return () => {
      try {
        abortRef.current?.abort();
      } catch {}
    };
  }, [fetchCategory]);

  /* -------------------- Desktop: Search handlers -------------------- */
  const handleSearch = useCallback(() => {
    const trimmed = (searchTerm || "").trim();
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (trimmed) {
        if (p.get("search") === trimmed) return p; // No change
        p.set("search", trimmed);
        p.set("page", "1");
        p.delete("category");
      } else {
        if (!p.has("search")) return p; // Already no search
        p.delete("search");
        p.set("page", "1");
      }
      return p;
    });
  }, [searchTerm, setSearchParams]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  useEffect(() => {
    if (isMobile) return;
    const currentSearch = searchParams.get("search") || "";
    const trimmedDebounced = (debouncedSearch || "").trim();

    if (trimmedDebounced !== currentSearch) {
      handleSearch();
    }
  }, [debouncedSearch, isMobile, searchParams, handleSearch]);

  /* -------------------- Mobile: click danh mục -------------------- */
  const handleCategoryClick = useCallback(
    (categoryItem) => {
      if (!categoryItem?.name) return;
      const slug = toSlug(categoryItem.name);
      setSearchTerm("");

      if (!isMobile) {
        setSearchParams({ category: slug, page: "1" });
        return;
      }
      setSelectedCategory(categoryItem);
      setCurrentPage(1);
      setTotalPages(1);
      setServices([]);
      fetchByCategory({ categoryId: categoryItem.id, page: 1, append: false });
    },
    [isMobile, setSearchParams, fetchByCategory],
  );

  /* -------------------- Mobile: Infinite scroll -------------------- */
  useEffect(() => {
    if (!isMobile) return;
    if (!sentinelRef.current) return;
    if (!selectedCategory?.id) return;

    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          !listLoading &&
          !loadingMore &&
          currentPage < totalPages
        ) {
          const nextPage = currentPage + 1;
          setLoadingMore(true);
          fetchByCategory({
            categoryId: selectedCategory.id,
            page: nextPage,
            append: true,
          });
          setCurrentPage(nextPage);
        }
      },
      { root: null, rootMargin: "0px 0px 200px 0px", threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    isMobile,
    selectedCategory,
    listLoading,
    loadingMore,
    currentPage,
    totalPages,
    fetchByCategory,
  ]);

  /* -------------------- Columns -------------------- */
  const columns = useMemo(
    () => [
      {
        key: "id",
        title: "STT",
        minWidth: "20px",
        align: "text-left",
        render: (value) => <div className="pl-2">{value}</div>,
      },
      {
        key: "ten",
        title: "Tên thủ tục",
        minWidth: "200px",
        render: (value) => <p className="truncate">{value}</p>,
      },
      { key: "ngayNopHoSo", title: "Thời gian nộp hồ sơ", minWidth: "150px" },
      { key: "ngayThamDinh", title: "Thời gian thẩm định", minWidth: "150px" },
      {
        key: "fileUrl",
        title: "Tài liệu",
        minWidth: "150px",
        render: (value) =>
          value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm text-blue-600 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Tài liệu
            </a>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
    ],
    [],
  );

  if (isMobile) {
    return (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Hướng dẫn thủ tục"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-104px)] bg-[#F2F2F2]">
          <motion.div
            className="h-[calc(100%-10px)] overflow-hidden rounded-xl"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="h-full overflow-y-auto">
              {bootLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loading />
                </div>
              ) : (
                <CategoryGrid
                  categories={category}
                  selectedCategory={selectedCategory}
                  onCategoryClick={handleCategoryClick}
                  onClosePopup={() => setSelectedCategory(null)}
                  data={services}
                  serviceLoading={listLoading}
                />
              )}

              <div ref={sentinelRef} className="h-6 w-full" />

              {loadingMore && (
                <div className="py-3 text-center text-sm text-gray-500">
                  Đang tải thêm...
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-full min-h-0 flex flex-col"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
    >
      <div className="shrink-0">
        <div className="mb-4 flex items-center justify-between">
          <motion.div
            className="flex w-3/5 gap-6 lap:2/5"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="relative flex w-full items-center">
              <label htmlFor="search-procedure" className="sr-only">
                Tìm kiếm thủ tục
              </label>
              <input
                id="search-procedure"
                type="text"
                placeholder="Tìm kiếm thủ tục"
                className="w-full rounded-lg border border-gray-200 px-4 py-2 focus:outline-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <motion.span
                className="absolute right-3 cursor-pointer rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
                onClick={handleSearch}
                role="button"
                aria-label="Thực hiện tìm kiếm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <img src="/icons/search-black.svg" alt="" aria-hidden="true" />
              </motion.span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
        >
          {bootLoading ? (
            <FilterButtonsSkeleton />
          ) : (
            <FilterButtons
              category={category}
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategoryClick}
            />
          )}
        </motion.div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto mt-4">
        {listLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="w-full px-2">
              <TableSkeleton rows={3} />
            </div>
          </div>
        ) : services?.length ? (
          <motion.div
            className="h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <CustomTable
              data={services || []}
              columns={columns}
              currentPage={currentPage}
              setCurrentPage={(page) => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set("page", page.toString());
                  return newParams;
                });

                try {
                  const scroller = document.querySelector(
                    ".guide-table-scroll",
                  );
                  scroller?.scrollTo({ top: 0, behavior: "smooth" });
                } catch {}
              }}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setView
              basePath="/huong-dan-thu-tuc/thu-tuc"
              rowClassName="transition-colors hover:bg-gray-50"
            />
          </motion.div>
        ) : (
          !bootLoading && (
            <EmptyState description="Không tìm thấy thủ tục phù hợp" />
          )
        )}
      </div>
    </motion.div>
  );
};

export default Guide;
